/**
 * WrapperEditor — structured editor for the REST-wrapper spec.
 *
 * One component, two modes:
 *   /build/wrappers/new                         → create a new wrapper spec
 *   /build/wrappers/:slug/edit                  → edit an existing one
 *
 * Replaces the old paste-a-JSON-blob wizard with a labeled form:
 *   - Top metadata: slug, name, description, owning WG
 *   - Auth realms editor (table with add/remove)
 *   - Tools editor (table with add/remove)
 *   - Realm-usage summary panel
 *   - Optional "JSON view" toggle for power users (round-trips with the form)
 *
 * Submit:
 *   create → POST /api/v1/wrapper-specs, navigate to credentials page
 *   edit   → PATCH /api/v1/wrapper-specs/:id, navigate back to server detail
 *
 * OpenAPI import button calls into a parser (./openapi-import.ts) and
 * pre-populates the form.
 */
import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Code2,
  FileJson,
  Plus,
  Trash2,
  Wand2,
} from 'lucide-react';

import {
  ApiError,
  useCreateWrapperSpec,
  useUpdateWrapperSpec,
  useUpsertWrapperCredential,
  useValidateWrapperSpec,
  useWorkgroups,
  useWrapperCredentials,
  useWrapperSpecBySlug,
  type CheckResult,
  type WrapperCredentialMode,
  type WrapperCredentialView,
} from '@/api/index.js';
import {
  cleanCredFields,
  credIsFillable,
  initialCredFieldsFromSaved,
  ModeFields,
  ModePicker,
  type CredFields,
} from '@/components/console/CredentialModeFields.js';
import { ActionButton } from '@/components/console/ActionButton.js';
import { HelpCallout } from '@/components/console/HelpCallout.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { Required, RequiredLegend } from '@/components/console/Required.js';
import { SectionCard } from '@/components/console/SectionCard.js';
import { AsyncShell } from '@/components/console/StateBlocks.js';
import { useDraftAutosave } from '@/hooks/useDraftAutosave.js';
import { openApiToWrapperSpec } from '@/lib/openapi-import.js';

const DATA_CLASSIFICATIONS = [
  'public',
  'internal',
  'confidential',
  'restricted',
  'regulated',
] as const;
const SIDE_EFFECTS = ['read_only', 'write_low', 'write_high', 'destructive'] as const;
const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;

interface RealmDraft {
  name: string;
  // Legacy hints retained for round-tripping JSON / OpenAPI imports.
  // The form no longer surfaces these; secrets are entered inline via
  // `credential` and POST'd to /api/v1/wrapper-credentials after spec
  // save. Existing specs that ship `token_url` etc. read through
  // unchanged.
  token_url?: string;
  client_id_env?: string;
  client_secret_env?: string;
  scope?: string;
  /**
   * Inline credential authoring state. Populated by the user via the
   * "Set credentials" drawer. After spec submit succeeds, each realm
   * with `credential.touched` triggers a PUT
   * /api/v1/wrapper-credentials/<slug>/<realm>. Never persisted in the
   * spec JSON itself.
   */
  credential?: {
    mode: WrapperCredentialMode;
    fields: CredFields;
    /** True once the user has opened/edited the drawer; used to decide whether to upsert. */
    touched: boolean;
  };
  /** Edit-mode protection — same as ToolDraft._locked. Hydrated realms start locked. */
  _locked?: boolean;
}

interface ToolDraft {
  name: string;
  description: string;
  method: string;
  url_template: string;
  auth_realm: string;
  data_classification: string;
  side_effect_level: string;
  input_schema_text: string; // JSON, edited as text
  query_text: string; // JSON, edited as text
  headers_text: string; // JSON, edited as text
  body_text: string; // JSON or empty
  restricted_to_wg_ids: string[];
  /**
   * Edit-mode protection: tools loaded from a saved spec start locked
   * and render as read-only summary cards with an "Unlock to edit"
   * button. Tools added in the current session start unlocked. Field
   * is stripped from the spec JSON before submit.
   */
  _locked?: boolean;
}

const DEFAULT_INPUT_SCHEMA = '{\n  "type": "object",\n  "properties": {}\n}';

const EMPTY_TOOL: ToolDraft = {
  name: '',
  description: '',
  method: 'GET',
  url_template: '',
  auth_realm: '',
  data_classification: 'internal',
  side_effect_level: 'read_only',
  input_schema_text: DEFAULT_INPUT_SCHEMA,
  query_text: '{}',
  headers_text: '{}',
  body_text: '',
  restricted_to_wg_ids: [],
};

export default function WrapperEditor(): JSX.Element {
  const navigate = useNavigate();
  const { slug: editSlug } = useParams<{ slug?: string }>();
  const editing = Boolean(editSlug);
  const existing = useWrapperSpecBySlug(editSlug);
  const wgs = useWorkgroups({ limit: 500 });
  const create = useCreateWrapperSpec();
  const update = useUpdateWrapperSpec();
  const validate = useValidateWrapperSpec();

  // Auto-save draft only when CREATING. On edit we always start from the
  // server's persisted spec — preserving an unrelated draft would confuse.
  const draftKey = editing ? `wrapper-edit-${editSlug}` : 'wrapper-new';
  const [draft, setDraft, draftRestored, clearDraft] = useDraftAutosave<{
    slug: string;
    name: string;
    description: string;
    owningWg: string;
    realms: RealmDraft[];
    tools: ToolDraft[];
  }>(draftKey, { slug: '', name: '', description: '', owningWg: '', realms: [], tools: [] });

  // Top-level fields (mirrored to draft).
  const slug = draft.slug;
  const setSlug = (v: string): void => setDraft({ ...draft, slug: v });
  const name = draft.name;
  const setName = (v: string): void => setDraft({ ...draft, name: v });
  const description = draft.description;
  const setDescription = (v: string): void => setDraft({ ...draft, description: v });
  const owningWg = draft.owningWg;
  const setOwningWg = (v: string): void => setDraft({ ...draft, owningWg: v });
  const realms = draft.realms;
  const setRealms = (v: RealmDraft[]): void => setDraft({ ...draft, realms: v });
  const tools = draft.tools;
  const setTools = (v: ToolDraft[]): void => setDraft({ ...draft, tools: v });

  const [activate, setActivate] = useState(false);
  const [openToolIdx, setOpenToolIdx] = useState<number | null>(null);
  const [openRealmIdx, setOpenRealmIdx] = useState<number | null>(null);
  const upsertCredential = useUpsertWrapperCredential();
  // On edit, prefetch the existing creds so realm rows show "Set" vs "Not set".
  const existingCredentials = useWrapperCredentials(editing ? editSlug : undefined);
  const savedCredentials = existingCredentials.data?.items ?? [];

  // JSON-view toggle (advanced)
  const [showJson, setShowJson] = useState(false);
  const [jsonText, setJsonText] = useState('');

  // OpenAPI import
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState<string | null>(null);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const checks = validate.data?.checks ?? null;
  const [validatedKey, setValidatedKey] = useState<string | null>(null);

  // Hydrate from existing spec on edit.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!editing) return;
    if (hydratedRef.current) return;
    if (!existing.data) return;
    const s = existing.data.spec as RawSpec;
    hydratedRef.current = true;
    setDraft({
      slug: existing.data.slug,
      name: existing.data.name,
      description: existing.data.description ?? '',
      owningWg: existing.data.owningWgId,
      realms: specRealmsToDrafts(s.auth_realms ?? {}).map((r) => ({ ...r, _locked: true })),
      // Hydrated tools start locked — accidental keystroke protection.
      // User clicks "Unlock to edit" per row to enable changes.
      tools: specToolsToDrafts(s.tools ?? []).map((t) => ({ ...t, _locked: true })),
    });
  }, [editing, existing.data, setDraft]);

  // Build the spec object from form state.
  const builtSpec = useMemo(
    () => buildSpecFromState({ slug, name, description, owningWg, realms, tools }),
    [slug, name, description, owningWg, realms, tools],
  );

  // Validation-signature gate: only allow Save when (a) the form passed
  // a Validate call AND (b) nothing has changed since that call. When
  // the user edits any field after a green Validate, the badge flips
  // back to "needs revalidation" and Save is disabled. (Backlog #4 —
  // also wanted on the M-Endpoint register form.)
  const builtSpecKey = useMemo(() => JSON.stringify(builtSpec), [builtSpec]);
  const validateGreen = checks !== null && checks.every((c) => c.severity !== 'error' || c.ok);
  const validatedClean = validatedKey !== null && validatedKey === builtSpecKey && validateGreen;

  // When entering JSON view, render the current spec; on leaving, parse + re-hydrate.
  useEffect(() => {
    if (showJson) setJsonText(JSON.stringify(builtSpec, null, 2));
  }, [showJson, builtSpec]);

  function applyJsonText(): boolean {
    try {
      const parsed = JSON.parse(jsonText) as RawSpec;
      setSlug(String(parsed.slug ?? ''));
      setName(String(parsed.name ?? ''));
      setDescription(String(parsed.description ?? ''));
      setOwningWg(String(parsed.owning_wg_id ?? ''));
      setRealms(specRealmsToDrafts(parsed.auth_realms ?? {}));
      setTools(specToolsToDrafts(parsed.tools ?? []));
      return true;
    } catch (e) {
      setSubmitError(`JSON parse error: ${(e as Error).message}`);
      return false;
    }
  }

  async function onSubmit(): Promise<void> {
    setSubmitError(null);
    if (showJson && !applyJsonText()) return;
    try {
      let savedSlug = '';
      let materializedServerId = '';
      if (editing && existing.data) {
        await update.mutateAsync({ id: existing.data.id, spec: builtSpec });
        savedSlug = existing.data.slug;
        materializedServerId = existing.data.materializedServerId ?? '';
      } else {
        const result = await create.mutateAsync({ spec: builtSpec, activate });
        savedSlug = result.spec.slug;
        materializedServerId = result.spec.materializedServerId ?? '';
      }

      // Sequence inline credential upserts AFTER the spec persists. We
      // never abort the navigate on a credential failure — the user lands
      // on the next page either way and we surface the per-realm error
      // there. Concrete: if FASM saves fine but FSS rejects (typo'd
      // token URL), the spec is already in place; user fixes FSS later.
      const credErrors: string[] = [];
      for (const r of realms) {
        if (!r.credential?.touched) continue;
        if (!credIsFillable(r.credential.mode, r.credential.fields)) continue;
        try {
          await upsertCredential.mutateAsync({
            slug: savedSlug,
            realm_name: r.name,
            mode: r.credential.mode,
            fields: cleanCredFields(r.credential.mode, r.credential.fields),
          });
        } catch (e) {
          credErrors.push(
            `realm "${r.name}": ${e instanceof ApiError ? `${e.status} ${JSON.stringify(e.body)}` : (e as Error).message}`,
          );
        }
      }
      clearDraft();
      if (credErrors.length > 0) {
        setSubmitError(
          `Spec saved, but ${credErrors.length} credential(s) failed:\n${credErrors.join('\n')}\n\nGo to the Credentials page to retry.`,
        );
        // Stay on the form so the user can read the error; don't navigate away.
        return;
      }
      // Land on the server detail page in both create+edit cases. Creds
      // are already saved inline; no detour. If for some reason the
      // materialized server id is missing (unexpected), fall back to
      // the spec list page rather than the credentials page.
      navigate(
        materializedServerId
          ? `/build/servers/${materializedServerId}`
          : `/catalog/toolkits/${encodeURIComponent(savedSlug)}`,
      );
    } catch (e) {
      setSubmitError(
        e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message,
      );
    }
  }

  function onValidate(): void {
    setSubmitError(null);
    if (showJson && !applyJsonText()) return;
    const keyAtCall = JSON.stringify(builtSpec);
    validate.mutate(
      { spec: builtSpec, currentSlug: editing ? editSlug : undefined },
      {
        onSuccess: (data) => {
          const allGreen = data.checks.every((c) => c.severity !== 'error' || c.ok);
          // Only gate-clean if validate came back without errors. Warns
          // are OK (they don't block submit).
          if (allGreen) setValidatedKey(keyAtCall);
          else setValidatedKey(null);
        },
      },
    );
  }

  function onImportOpenApi(): void {
    setImportError(null);
    try {
      const result = openApiToWrapperSpec(importText, {
        defaultOwningWgId: owningWg || undefined,
        suggestedSlug: slug || undefined,
      });
      // Merge: keep current slug/name/wg if set; otherwise use imported.
      if (!slug) setSlug(result.slug);
      if (!name) setName(result.name);
      if (!owningWg) setOwningWg(result.owning_wg_id ?? '');
      // Realms: union by name (existing wins on conflict).
      const existingNames = new Set(realms.map((r) => r.name));
      const importedRealms: RealmDraft[] = Object.entries(result.auth_realms ?? {})
        .filter(([n]) => !existingNames.has(n))
        .map(([n, r]) => ({ ...(r as RealmDraft), name: n }));
      setRealms([...realms, ...importedRealms]);
      // Tools: append imported ones.
      const importedTools = result.tools.map((t) => specToolToDraft(t));
      setTools([...tools, ...importedTools]);
      setImportOpen(false);
      setImportText('');
    } catch (e) {
      setImportError((e as Error).message);
    }
  }

  // Realm-usage summary
  const realmUsage = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of realms) counts[r.name] = 0;
    for (const t of tools) {
      const k = t.auth_realm || '(none)';
      counts[k] = (counts[k] ?? 0) + 1;
    }
    return counts;
  }, [realms, tools]);

  if (editing && existing.isLoading) {
    return (
      <>
        <PageHeader eyebrow="Build" title="Edit wrapper" description="Loading…" />
        <AsyncShell
          isLoading={true}
          isError={false}
          isEmpty={false}
          loadingTitle="Loading spec"
          emptyTitle=""
        >
          <span />
        </AsyncShell>
      </>
    );
  }
  if (editing && !existing.data && !existing.isLoading) {
    return (
      <>
        <PageHeader
          eyebrow="Build"
          title="Edit wrapper"
          description={`No wrapper found with slug "${editSlug}".`}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Build"
        title={editing ? `Edit wrapper — ${editSlug}` : 'New REST wrapper'}
        description={
          editing
            ? 'Add or change tools, realms, and metadata. Save patches the spec; the wrapper service picks up changes within 30s.'
            : 'Step 1 of 2: build the spec. Step 2 (next page) collects auth secrets via UI.'
        }
      >
        {editing && (
          <Link to={`/build/servers/${existing.data?.materializedServerId ?? ''}`}>
            <ActionButton icon={ArrowLeft}>Back to server</ActionButton>
          </Link>
        )}
        {!editing && (
          <Link to="/build/register">
            <ActionButton icon={ArrowLeft}>Change mode</ActionButton>
          </Link>
        )}
      </PageHeader>

      {draftRestored && !editing && (
        <div
          style={{
            padding: 10,
            background: 'rgba(70, 100, 140, 0.06)',
            border: '1px solid rgba(70, 100, 140, 0.2)',
            borderRadius: 8,
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <strong>Draft restored.</strong> Your in-progress wrapper from the previous session has
          been recovered.{' '}
          <button
            type="button"
            onClick={() => clearDraft()}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#c0392b',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Discard draft
          </button>
        </div>
      )}

      <HelpCallout
        title="How to use this editor"
        storageKey="wrapper-editor"
        whatHappens={
          <>
            Each row in <strong>Tools</strong> becomes one MCP tool. Each row in{' '}
            <strong>Auth realms</strong> is a credential bucket your tools can reference. After
            save, fill in real secrets per realm on the Credentials page; the wrapper service
            decrypts them at call time.
          </>
        }
        youNeed={[
          <>A unique slug (lowercase, dashes only).</>,
          <>
            An owning WG (create one in <a href="/admin/workgroups">Admin → Workgroups</a> if
            needed).
          </>,
          <>One or more tools with HTTP method + URL template + (optionally) an auth realm.</>,
          <>For OAuth/API-key tools: declare a realm now; secrets go in next.</>,
        ]}
        afterwards={[
          <>
            Click <strong>Validate</strong> to sanity-check before submit.
          </>,
          <>
            <strong>Save</strong> creates/updates the spec.{' '}
            {editing
              ? 'You stay; navigate back to the server detail.'
              : "On create, you're sent to the Credentials page automatically."}
          </>,
        ]}
      />

      {!editing && (
        <div
          style={{
            marginBottom: 12,
            padding: 12,
            border: '1px dashed var(--line)',
            borderRadius: 8,
            background: 'rgba(70, 100, 140, 0.04)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ fontSize: 13 }}>
            <strong>Already have a spec?</strong> Paste / upload a wrapper-spec JSON or an OpenAPI
            3.x doc — we'll auto-populate every field below.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => setShowJson(true)} style={btnSecondary}>
              <Code2 size={13} style={{ marginRight: 6 }} /> Paste JSON spec
            </button>
            <button type="button" onClick={() => setImportOpen(true)} style={btnSecondary}>
              <FileJson size={13} style={{ marginRight: 6 }} /> Import OpenAPI
            </button>
          </div>
        </div>
      )}

      <SectionCard
        icon={Wand2}
        title="Identity"
        text="Slug is the public path: /wrappers/<slug>/mcp. Immutable on edit."
      >
        <RequiredLegend />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Slug" required>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              disabled={editing}
              placeholder="stanford-financials"
              style={inputStyle}
              required
            />
          </Field>
          <Field label="Display name" required>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Stanford Financials"
              style={inputStyle}
              required
            />
          </Field>
          <Field label="Owning workgroup" required>
            <select
              value={owningWg}
              onChange={(e) => setOwningWg(e.target.value)}
              style={inputStyle}
              required
            >
              <option value="">— pick a workgroup —</option>
              {(wgs.data?.items ?? []).map((w) => (
                <option key={w.id} value={w.id}>
                  {w.id} — {w.display_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Description (optional)">
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={inputStyle}
            />
          </Field>
        </div>
      </SectionCard>

      <SectionCard
        icon={FileJson}
        title="Auth realms"
        text="A realm is a credential bucket your tools reference. Set credentials inline — they're encrypted at rest and never returned over the API after save. Skip if a tool needs no auth."
      >
        <div style={{ display: 'grid', gap: 8 }}>
          {realms.length === 0 && (
            <p className="subtle" style={{ fontSize: 13 }}>
              No realms yet. Add one if any of your tools need auth (OAuth, API key, basic, …).
            </p>
          )}
          {realms.map((r, idx) => (
            <RealmRow
              key={idx}
              realm={r}
              savedCredential={savedCredentialFor(r.name, savedCredentials)}
              isOpen={openRealmIdx === idx}
              onToggleOpen={() => setOpenRealmIdx(openRealmIdx === idx ? null : idx)}
              onChange={(next) => {
                const arr = [...realms];
                arr[idx] = next;
                setRealms(arr);
              }}
              onRemove={() => {
                setRealms(realms.filter((_, i) => i !== idx));
                if (openRealmIdx === idx) setOpenRealmIdx(null);
              }}
            />
          ))}
          <button
            type="button"
            onClick={() => setRealms([...realms, { name: '' }])}
            style={{ ...btnSecondary, alignSelf: 'flex-start' }}
          >
            <Plus size={13} style={{ marginRight: 6 }} /> Add realm
          </button>

          {Object.keys(realmUsage).length > 0 && (
            <div
              style={{
                marginTop: 8,
                padding: 10,
                background: 'rgba(0,0,0,0.04)',
                borderRadius: 8,
                fontSize: 13,
              }}
            >
              <strong>Realm usage:</strong>{' '}
              {Object.entries(realmUsage)
                .map(([n, c]) => `${n} = ${c} tool${c === 1 ? '' : 's'}`)
                .join(' · ')}
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard
        icon={Code2}
        title={`Tools (${tools.length})`}
        text="One row = one MCP tool. Click a row to expand and edit input schema, headers, query, and body templates."
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <button type="button" onClick={() => setImportOpen(true)} style={btnSecondary}>
            <FileJson size={13} style={{ marginRight: 6 }} /> Import from OpenAPI
          </button>
          <button
            type="button"
            onClick={() => setShowJson((s) => !s)}
            style={btnSecondary}
            title="Power-user view"
          >
            <Code2 size={13} style={{ marginRight: 6 }} /> {showJson ? 'Form view' : 'JSON view'}
          </button>
        </div>

        {showJson ? (
          <textarea
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            rows={24}
            spellCheck={false}
            style={{
              ...inputStyle,
              fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
              fontSize: 12,
              resize: 'vertical',
            }}
          />
        ) : (
          <ToolsTable
            tools={tools}
            setTools={setTools}
            realms={realms}
            openIdx={openToolIdx}
            setOpenIdx={setOpenToolIdx}
          />
        )}

        {importOpen && (
          <ImportPanel
            text={importText}
            setText={setImportText}
            onImport={onImportOpenApi}
            onCancel={() => {
              setImportOpen(false);
              setImportError(null);
            }}
            error={importError}
          />
        )}
      </SectionCard>

      {checks && <ChecklistPanel checks={checks} />}

      {validatedKey !== null && validatedKey !== builtSpecKey && (
        <p
          style={{
            fontSize: 13,
            color: '#92400e',
            background: '#fef3c7',
            border: '1px solid #f5b700',
            borderRadius: 6,
            padding: '8px 12px',
            margin: '8px 0',
          }}
          role="status"
        >
          <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          You've edited the spec since the last successful Validate. Click <strong>
            Validate
          </strong>{' '}
          again before Save.
        </p>
      )}

      {submitError && (
        <p style={{ color: '#c0392b', fontSize: 14 }} role="alert">
          <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {submitError}
        </p>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        {!editing && (
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 14,
              marginRight: 'auto',
            }}
          >
            <input
              type="checkbox"
              checked={activate}
              onChange={(e) => setActivate(e.target.checked)}
            />
            Activate immediately (skip draft)
          </label>
        )}
        <button
          type="button"
          onClick={onValidate}
          disabled={validate.isPending}
          style={btnSecondary}
        >
          {validate.isPending ? 'Validating…' : 'Validate'}
        </button>
        {(() => {
          const saveDisabled =
            create.isPending ||
            update.isPending ||
            // In form view, gate on the labeled-form fields. In JSON
            // view, defer to server-side validation since the source of
            // truth is the textarea (form fields might lag).
            (!showJson && (!slug || !name || !owningWg)) ||
            (showJson && jsonText.trim().length === 0) ||
            // Validation gate: must have a green Validate AND no edits
            // since. Prevents shipping a spec the form thinks is fine
            // but the server already said no to.
            !validatedClean;
          return (
            <button
              type="button"
              onClick={onSubmit}
              disabled={saveDisabled}
              title={
                !validatedClean
                  ? 'Click Validate first; if you edit after Validate, click Validate again before Save.'
                  : ''
              }
              style={{
                ...btnPrimary,
                ...(saveDisabled
                  ? {
                      background: '#cbd5e1',
                      borderColor: '#9ca3af',
                      color: '#6b7280',
                      cursor: 'not-allowed',
                      opacity: 0.85,
                    }
                  : null),
              }}
            >
              <Check size={13} style={{ marginRight: 6 }} />
              {create.isPending || update.isPending
                ? 'Saving…'
                : editing
                  ? 'Save changes'
                  : activate
                    ? 'Save + activate'
                    : 'Save (draft)'}
            </button>
          );
        })()}
      </div>
    </>
  );
}

/* ── Tools table ──────────────────────────────────────────────────── */

function ToolsTable({
  tools,
  setTools,
  realms,
  openIdx,
  setOpenIdx,
}: {
  tools: ToolDraft[];
  setTools: (n: ToolDraft[]) => void;
  realms: RealmDraft[];
  openIdx: number | null;
  setOpenIdx: (n: number | null) => void;
}): JSX.Element {
  const update = (idx: number, patch: Partial<ToolDraft>): void => {
    const next = [...tools];
    next[idx] = { ...tools[idx]!, ...patch };
    setTools(next);
  };
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {tools.length === 0 && (
        <p className="subtle" style={{ fontSize: 13 }}>
          No tools yet. Click <strong>Add tool</strong>, or import from an OpenAPI spec.
        </p>
      )}
      {tools.map((t, idx) => {
        const descLen = t.description.trim().length;
        const descOk = descLen >= 30;
        if (t._locked) {
          return (
            <div
              key={idx}
              style={{
                border: '1px solid var(--line)',
                borderRadius: 8,
                background: 'rgba(0,0,0,0.025)',
                padding: 10,
                display: 'grid',
                gridTemplateColumns: 'auto auto auto 1fr auto auto auto',
                alignItems: 'center',
                gap: 10,
                fontSize: 13,
                minWidth: 0,
                overflow: 'hidden',
              }}
              title="Existing tool — locked to prevent accidental edits. Click Unlock to modify."
            >
              <span style={{ opacity: 0.55 }}>🔒</span>
              <code
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 240,
                }}
              >
                {t.name}
              </code>
              <span
                style={{
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: 11,
                  background: 'rgba(0,0,0,0.05)',
                  padding: '2px 6px',
                  borderRadius: 4,
                  color: '#374151',
                }}
              >
                {t.method}
              </span>
              <span
                style={{
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: 12,
                  color: '#6b7280',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {t.url_template || '(no URL)'}
              </span>
              <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
                {t.auth_realm ? (
                  <>
                    via <code>{t.auth_realm}</code>
                  </>
                ) : (
                  '(no auth)'
                )}
              </span>
              <button
                type="button"
                onClick={() => update(idx, { _locked: false })}
                style={{ ...btnSecondary, flexShrink: 0 }}
                title="Unlock this tool to edit fields"
              >
                Unlock
              </button>
              <button
                type="button"
                onClick={() => setTools(tools.filter((_, i) => i !== idx))}
                style={{ ...btnDanger, flexShrink: 0 }}
                title="Remove tool"
              >
                <Trash2 size={13} />
              </button>
            </div>
          );
        }
        return (
          <div
            key={idx}
            style={{
              border: '1px solid var(--line)',
              borderRadius: 8,
              background: 'rgba(255,255,255,0.5)',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1.2fr 80px 2fr 160px 40px 40px',
                gap: 6,
                padding: 8,
                alignItems: 'center',
              }}
            >
              <input
                value={t.name}
                onChange={(e) => update(idx, { name: e.target.value })}
                placeholder="tool_name"
                style={inputStyle}
              />
              <select
                value={t.method}
                onChange={(e) => update(idx, { method: e.target.value })}
                style={inputStyle}
              >
                {HTTP_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <input
                value={t.url_template}
                onChange={(e) => update(idx, { url_template: e.target.value })}
                placeholder="https://api.example.com/foo/{{args.id}}"
                style={inputStyle}
              />
              <select
                value={t.auth_realm}
                onChange={(e) => update(idx, { auth_realm: e.target.value })}
                style={inputStyle}
              >
                <option value="">(no auth)</option>
                {realms.map((r) => (
                  <option key={r.name} value={r.name}>
                    {r.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                style={btnSecondary}
                title={
                  openIdx === idx ? 'Collapse advanced' : 'Edit advanced (schema, headers, body)'
                }
              >
                {openIdx === idx ? '−' : '⋯'}
              </button>
              <button
                type="button"
                onClick={() => setTools(tools.filter((_, i) => i !== idx))}
                style={btnDanger}
                title="Remove tool"
              >
                <Trash2 size={13} />
              </button>
            </div>
            {/* LLM Instruction — always visible (it's the most important field). */}
            <div style={{ padding: '0 8px 10px' }}>
              <label style={{ display: 'block' }}>
                <span
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    fontSize: 13,
                    marginBottom: 4,
                  }}
                >
                  <span>
                    <strong>LLM Instruction</strong>
                    <Required />
                    <span className="subtle" style={{ marginLeft: 6 }}>
                      — the LLM reads this to decide WHEN to call the tool
                    </span>
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      color: descOk ? '#2f855a' : descLen === 0 ? '#999' : '#b7791f',
                    }}
                  >
                    {descLen} chars {descOk ? '✓' : `(min 30)`}
                  </span>
                </span>
                <textarea
                  value={t.description}
                  onChange={(e) => update(idx, { description: e.target.value })}
                  rows={4}
                  placeholder={`USE WHEN: …describe the user-intent that should trigger this tool…\n\nDO NOT USE FOR: …things this looks like but isn't…\n\nRETURNS: …shape of the response…`}
                  style={{
                    ...inputStyle,
                    resize: 'vertical',
                    borderColor: descOk ? 'var(--line)' : descLen > 0 ? '#e6b07a' : 'var(--line)',
                  }}
                />
              </label>
            </div>
            {openIdx === idx && (
              <div style={{ padding: 12, borderTop: '1px solid var(--line)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Data classification">
                    <select
                      value={t.data_classification}
                      onChange={(e) => update(idx, { data_classification: e.target.value })}
                      style={inputStyle}
                    >
                      {DATA_CLASSIFICATIONS.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                    <DataClassHelp value={t.data_classification} />
                  </Field>
                  <Field label="Side effect level">
                    <select
                      value={t.side_effect_level}
                      onChange={(e) => update(idx, { side_effect_level: e.target.value })}
                      style={inputStyle}
                    >
                      {SIDE_EFFECTS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                    <SideEffectHelp value={t.side_effect_level} />
                  </Field>
                </div>
                <Field label="Input schema (JSON Schema)">
                  <textarea
                    value={t.input_schema_text}
                    onChange={(e) => update(idx, { input_schema_text: e.target.value })}
                    onFocus={(e) => {
                      // If the field still holds the boilerplate `{ "type": "object",
                      // "properties": {} }`, clear it on first focus so the user can
                      // paste their actual schema without manually deleting.
                      if (e.target.value.trim() === DEFAULT_INPUT_SCHEMA.trim()) {
                        update(idx, { input_schema_text: '' });
                      }
                    }}
                    placeholder={DEFAULT_INPUT_SCHEMA}
                    rows={6}
                    style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                  />
                </Field>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Query params (JSON)">
                    <textarea
                      value={t.query_text}
                      onChange={(e) => update(idx, { query_text: e.target.value })}
                      rows={4}
                      style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                    />
                  </Field>
                  <Field label="Headers (JSON)">
                    <textarea
                      value={t.headers_text}
                      onChange={(e) => update(idx, { headers_text: e.target.value })}
                      rows={4}
                      style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                    />
                  </Field>
                </div>
                <Field label="Body (JSON, only for POST/PUT/PATCH)">
                  <textarea
                    value={t.body_text}
                    onChange={(e) => update(idx, { body_text: e.target.value })}
                    rows={4}
                    style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                  />
                </Field>
              </div>
            )}
          </div>
        );
      })}
      <button
        type="button"
        onClick={() => setTools([...tools, { ...EMPTY_TOOL }])}
        style={{ ...btnSecondary, alignSelf: 'flex-start' }}
      >
        <Plus size={13} style={{ marginRight: 6 }} /> Add tool
      </button>
    </div>
  );
}

/* ── Import panel ─────────────────────────────────────────────────── */

function ImportPanel({
  text,
  setText,
  onImport,
  onCancel,
  error,
}: {
  text: string;
  setText: (v: string) => void;
  onImport: () => void;
  onCancel: () => void;
  error: string | null;
}): JSX.Element {
  return (
    <div
      style={{
        marginTop: 12,
        border: '1px dashed var(--line)',
        borderRadius: 8,
        padding: 12,
        background: 'rgba(70, 100, 140, 0.04)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong style={{ fontSize: 14 }}>Import from OpenAPI 3.x</strong>
        <button type="button" onClick={onCancel} style={btnSecondary}>
          Cancel
        </button>
      </div>
      <p className="subtle" style={{ fontSize: 13, margin: '6px 0' }}>
        Paste an OpenAPI YAML or JSON document. We'll create one tool per operation, derive auth
        realms from <code>securitySchemes</code>, and pre-fill input schemas. Governance fields
        (data classification, side effect) default to <code>internal</code> + <code>read_only</code>{' '}
        — review per tool after import.
      </p>
      <label style={{ display: 'inline-block', marginBottom: 6 }}>
        <input
          type="file"
          accept=".json,.yaml,.yml,application/json,text/yaml"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            f.text()
              .then((t) => setText(t))
              .catch(() => {});
          }}
          style={{ fontSize: 13 }}
        />
      </label>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={12}
        placeholder="paste openapi.yaml or openapi.json here"
        style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
      />
      {error && (
        <p style={{ color: '#c0392b', fontSize: 13, margin: '6px 0 0' }}>
          <AlertTriangle size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {error}
        </p>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button type="button" onClick={onImport} disabled={!text.trim()} style={btnPrimary}>
          <Check size={13} style={{ marginRight: 6 }} /> Import
        </button>
      </div>
    </div>
  );
}

/* ── Checklist panel (mirrors the one in Register) ───────────────── */

function ChecklistPanel({ checks }: { checks: CheckResult[] }): JSX.Element {
  const errs = checks.filter((c) => c.severity === 'error' && !c.ok).length;
  const warns = checks.filter((c) => c.severity === 'warn' && !c.ok).length;
  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 8,
        padding: 12,
        background: 'rgba(255,255,255,0.5)',
        fontSize: 13,
        marginTop: 8,
      }}
    >
      <strong>
        Validation: {checks.length} checks ({errs} error, {warns} warn)
      </strong>
      <ul style={{ margin: '8px 0 0', paddingLeft: 0, listStyle: 'none' }}>
        {checks.map((c) => {
          const color =
            c.severity === 'error' && !c.ok
              ? '#c0392b'
              : c.severity === 'warn' && !c.ok
                ? '#b7791f'
                : '#2f855a';
          const icon = c.ok ? '✓' : c.severity === 'error' ? '✗' : '⚠';
          return (
            <li
              key={c.name}
              style={{ display: 'flex', gap: 8, padding: '3px 0', alignItems: 'flex-start' }}
            >
              <span style={{ color, width: 14, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
              <span style={{ flex: 1 }}>
                <code style={{ marginRight: 6 }}>{c.name}</code>
                <span style={{ color }}>{c.message}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ── Realm row (with inline credential drawer) ──────────────────── */

function savedCredentialFor(
  realmName: string,
  list: WrapperCredentialView[],
): WrapperCredentialView | null {
  return list.find((c) => c.realm_name === realmName) ?? null;
}

function RealmRow({
  realm,
  savedCredential,
  isOpen,
  onToggleOpen,
  onChange,
  onRemove,
}: {
  realm: RealmDraft;
  savedCredential: WrapperCredentialView | null;
  isOpen: boolean;
  onToggleOpen: () => void;
  onChange: (next: RealmDraft) => void;
  onRemove: () => void;
}): JSX.Element {
  // Default mode: OAuth client_credentials matches the most common case
  // (every realm we've shipped so far). Users pick `api_key_header` etc. inline.
  const cred =
    realm.credential ??
    ({
      mode: 'oauth_client_credentials' as WrapperCredentialMode,
      fields: initialCredFieldsFromSaved(savedCredential),
      touched: false,
    } as NonNullable<RealmDraft['credential']>);
  const credState: 'pending' | 'saved' | 'unset' = realm.credential?.touched
    ? 'pending'
    : savedCredential
      ? 'saved'
      : 'unset';
  const pillStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: 999,
    border: '1px solid',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    ...(credState === 'pending'
      ? { background: '#fef3c7', borderColor: '#f5b700', color: '#92400e' }
      : credState === 'saved'
        ? { background: '#d1fae5', borderColor: '#34d399', color: '#065f46' }
        : { background: '#f3f4f6', borderColor: 'var(--line)', color: '#6b7280' }),
  };
  const credPillText =
    credState === 'pending'
      ? '⏱ Pending'
      : credState === 'saved'
        ? `✓ ${savedCredential!.mode}`
        : '○ No credential';
  const credBtnText = isOpen ? '− Close' : credState !== 'unset' ? 'Edit' : 'Add credential';
  if (realm._locked) {
    return (
      <div
        style={{
          border: '1px solid var(--line)',
          borderRadius: 8,
          background: 'rgba(0,0,0,0.025)',
          padding: 10,
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto auto auto',
          alignItems: 'center',
          gap: 10,
          fontSize: 13,
          minWidth: 0,
          overflow: 'hidden',
        }}
        title="Existing realm — locked. Click Unlock to rename or rotate the credential."
      >
        <span style={{ opacity: 0.55 }}>🔒</span>
        <code
          style={{
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
        >
          {realm.name}
        </code>
        <span style={pillStyle}>{credPillText}</span>
        <button
          type="button"
          onClick={() => onChange({ ...realm, _locked: false })}
          style={{ ...btnSecondary, flexShrink: 0 }}
          title="Unlock to rename or rotate"
        >
          Unlock
        </button>
        <button
          type="button"
          onClick={onRemove}
          style={{ ...btnDanger, flexShrink: 0 }}
          title="Remove realm"
        >
          <Trash2 size={13} />
        </button>
      </div>
    );
  }
  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 8,
        background: 'rgba(255,255,255,0.5)',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 8,
          padding: 8,
          alignItems: 'center',
          flexWrap: 'nowrap',
        }}
      >
        <input
          value={realm.name}
          onChange={(e) => onChange({ ...realm, name: e.target.value })}
          placeholder="realm name (e.g. fss, fasm, fingate)"
          style={{ ...inputStyle, flex: '1 1 200px', minWidth: 0 }}
        />
        <span
          style={pillStyle}
          title={
            credState === 'pending'
              ? 'This credential will be saved automatically when you click Save below.'
              : credState === 'saved'
                ? 'Stored encrypted in the gateway secrets vault.'
                : 'Click Add credential to enter token URL / client ID / secret etc.'
          }
        >
          {credPillText}
        </span>
        <button type="button" onClick={onToggleOpen} style={{ ...btnSecondary, flexShrink: 0 }}>
          {credBtnText}
        </button>
        <button
          type="button"
          onClick={onRemove}
          style={{ ...btnDanger, flexShrink: 0 }}
          title="Remove realm"
        >
          <Trash2 size={13} />
        </button>
      </div>
      {isOpen && (
        <div
          style={{
            padding: 12,
            borderTop: '1px solid var(--line)',
            background: 'rgba(0,0,0,0.02)',
          }}
        >
          <p className="subtle" style={{ fontSize: 12, marginTop: 0 }}>
            Credentials are encrypted at rest with the gateway's secrets key. After save, the API
            never returns the secret — only metadata (mode, header name, token URL, …). Leave blank
            to keep an existing credential unchanged.
          </p>
          <ModePicker
            mode={cred.mode}
            setMode={(m) => onChange({ ...realm, credential: { ...cred, mode: m, touched: true } })}
          />
          <ModeFields
            mode={cred.mode}
            fields={cred.fields}
            setFields={(next) =>
              onChange({ ...realm, credential: { ...cred, fields: next, touched: true } })
            }
            saved={savedCredential}
          />
        </div>
      )}
    </div>
  );
}

/* ── Governance dropdown help ────────────────────────────────────── */

const DATA_CLASS_HELP: Record<string, string> = {
  public: 'Anyone — no Stanford auth needed. Rare (e.g. published statistics).',
  internal: 'Stanford community broadly. Default for non-sensitive tools.',
  confidential:
    'Financial / HR / business-sensitive. Most P2P / FMS lookups land here (tied to user identity).',
  restricted: 'FERPA / PHI / pre-tokenized payment cards. Extra approval required.',
  regulated: 'Legally restricted (PII, tax, export-controlled). Highest scrutiny.',
};

const SIDE_EFFECT_HELP: Record<string, string> = {
  read_only: 'Pure query, no state change. Validators, lookups, search.',
  write_low: 'Small additive change — log feedback, add a note. Reversible.',
  write_high: 'Significant state change — create requisition, submit approval.',
  destructive: 'Irreversible — delete, dispatch payment, terminate. Requires approval.',
};

function DataClassHelp({ value }: { value: string }): JSX.Element {
  return (
    <p className="subtle" style={{ fontSize: 12, margin: '4px 0 0' }}>
      {DATA_CLASS_HELP[value] ?? ''}
    </p>
  );
}
function SideEffectHelp({ value }: { value: string }): JSX.Element {
  return (
    <p className="subtle" style={{ fontSize: 12, margin: '4px 0 0' }}>
      {SIDE_EFFECT_HELP[value] ?? ''}
    </p>
  );
}

/* ── Helpers / styles ─────────────────────────────────────────────── */

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label style={{ display: 'block', marginBottom: 8 }}>
      <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
        {label}
        {required && <Required />}
      </span>
      {children}
    </label>
  );
}

interface RawSpec {
  slug?: string;
  name?: string;
  description?: string;
  owning_wg_id?: string;
  auth_realms?: Record<string, Partial<RealmDraft>>;
  tools?: RawTool[];
}
interface RawTool {
  name?: string;
  description?: string;
  input_schema?: unknown;
  output_schema?: unknown;
  data_classification?: string;
  side_effect_level?: string;
  auth_realm?: string;
  request?: {
    method?: string;
    url_template?: string;
    headers?: Record<string, string>;
    query?: Record<string, string>;
    body?: unknown;
  };
  restricted_to_wg_ids?: string[];
}

function specRealmsToDrafts(realms: Record<string, Partial<RealmDraft>>): RealmDraft[] {
  return Object.entries(realms).map(([name, r]) => ({
    name,
    token_url: r.token_url,
    client_id_env: r.client_id_env,
    client_secret_env: r.client_secret_env,
    scope: r.scope,
  }));
}

function specToolsToDrafts(tools: RawTool[]): ToolDraft[] {
  return tools.map(specToolToDraft);
}

function specToolToDraft(t: RawTool): ToolDraft {
  return {
    name: String(t.name ?? ''),
    description: String(t.description ?? ''),
    method: String(t.request?.method ?? 'GET').toUpperCase(),
    url_template: String(t.request?.url_template ?? ''),
    auth_realm: String(t.auth_realm ?? ''),
    data_classification: String(t.data_classification ?? 'internal'),
    side_effect_level: String(t.side_effect_level ?? 'read_only'),
    input_schema_text: stringifyJson(
      t.input_schema,
      '{\n  "type": "object",\n  "properties": {}\n}',
    ),
    query_text: stringifyJson(t.request?.query, '{}'),
    headers_text: stringifyJson(t.request?.headers, '{}'),
    body_text: t.request?.body !== undefined ? stringifyJson(t.request.body, '') : '',
    restricted_to_wg_ids: t.restricted_to_wg_ids ?? [],
  };
}

function stringifyJson(v: unknown, fallback: string): string {
  if (v === undefined || v === null) return fallback;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return fallback;
  }
}

function buildSpecFromState(state: {
  slug: string;
  name: string;
  description: string;
  owningWg: string;
  realms: RealmDraft[];
  tools: ToolDraft[];
}): unknown {
  const auth_realms: Record<string, Record<string, unknown>> = {};
  for (const r of state.realms) {
    if (!r.name) continue;
    auth_realms[r.name] = {};
    if (r.token_url) auth_realms[r.name].token_url = r.token_url;
    // We keep client_id_env / scope etc. only if present (legacy compat).
    if (r.client_id_env) auth_realms[r.name].client_id_env = r.client_id_env;
    if (r.client_secret_env) auth_realms[r.name].client_secret_env = r.client_secret_env;
    if (r.scope) auth_realms[r.name].scope = r.scope;
  }
  const tools = state.tools.map((t) => {
    const safeJson = (text: string, fallback: unknown): unknown => {
      try {
        return text.trim() ? JSON.parse(text) : fallback;
      } catch {
        return fallback;
      }
    };
    const tool: Record<string, unknown> = {
      name: t.name,
      description: t.description,
      input_schema: safeJson(t.input_schema_text, { type: 'object' }),
      data_classification: t.data_classification,
      side_effect_level: t.side_effect_level,
      request: {
        method: t.method.toUpperCase(),
        url_template: t.url_template,
        headers: safeJson(t.headers_text, {}),
        query: safeJson(t.query_text, {}),
        ...(t.body_text.trim() ? { body: safeJson(t.body_text, {}) } : {}),
      },
    };
    if (t.auth_realm) tool.auth_realm = t.auth_realm;
    if (t.restricted_to_wg_ids.length > 0) tool.restricted_to_wg_ids = t.restricted_to_wg_ids;
    return tool;
  });
  return {
    slug: state.slug,
    name: state.name,
    ...(state.description ? { description: state.description } : {}),
    owning_wg_id: state.owningWg,
    ...(Object.keys(auth_realms).length > 0 ? { auth_realms } : {}),
    tools,
  };
}

const inputStyle: React.CSSProperties = {
  padding: '6px 8px',
  border: '1px solid var(--line)',
  borderRadius: 6,
  fontSize: 13,
  background: 'transparent',
  color: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
};
const btnPrimary: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 8,
  border: '1px solid rgba(140, 21, 21, 0.3)',
  background: 'linear-gradient(135deg, #8c1515, #b83a24)',
  color: '#fff8ec',
  fontWeight: 650,
  cursor: 'pointer',
  fontSize: 14,
  display: 'inline-flex',
  alignItems: 'center',
};
const btnSecondary: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid var(--line-strong)',
  background: 'rgba(255,255,255,0.85)',
  fontSize: 13,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
};
const btnDanger: React.CSSProperties = {
  ...btnSecondary,
  border: '1px solid #d97766',
  color: '#c0392b',
};
